import { useState, useEffect } from 'react'
import { Search, RefreshCw, Settings, Check, Loader2, Menu, X } from 'lucide-react'
import SettingsPanel from '../Settings/SettingsPanel'
import { syncEmailsChunked, getSyncStatus, SyncStatus } from '../../services/emailService'

interface HeaderProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  onRefresh?: () => Promise<void>
  isRefreshing?: boolean
  onMenuClick?: () => void
}

export default function Header({
  searchQuery,
  onSearchChange,
  onRefresh,
  isRefreshing = false,
  onMenuClick
}: HeaderProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)

  // Load initial sync status
  useEffect(() => {
    loadSyncStatus()
  }, [])

  const loadSyncStatus = async () => {
    const status = await getSyncStatus()
    setSyncStatus(status)
  }

  const handleRefresh = async () => {
    if (isSyncing) return

    setIsSyncing(true)
    setSyncMessage('Starting sync...')

    try {
      let isComplete = false
      let iterations = 0
      const maxIterations = 20 // Safety limit

      while (!isComplete && iterations < maxIterations) {
        iterations++
        const result = await syncEmailsChunked(200, 'INBOX')

        setSyncMessage(result.message)
        setSyncStatus({
          totalSynced: result.totalSynced,
          oldestDate: result.oldestDate || null,
          isComplete: result.isComplete,
          retentionDays: 30,
          lastSyncAt: new Date().toISOString()
        })

        isComplete = result.isComplete

        if (!isComplete) {
          // Small delay between chunks
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      // Also sync sent folder (one chunk)
      setSyncMessage('Syncing sent emails...')
      await syncEmailsChunked(100, 'SENT')

      setSyncMessage(null)

      // Trigger parent refresh to reload UI
      if (onRefresh) {
        await onRefresh()
      }

      // Reload status
      await loadSyncStatus()
    } catch (error) {
      console.error('Sync failed:', error)
      setSyncMessage('Sync failed')
    } finally {
      setIsSyncing(false)
      // Clear message after a delay
      setTimeout(() => setSyncMessage(null), 3000)
    }
  }

  const isLoading = isSyncing || isRefreshing

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 lg:px-6">
      {/* Left Section - Menu button (mobile) + Search */}
      <div className="flex items-center flex-1 gap-2 lg:gap-4">
        {/* Hamburger Menu - Mobile Only */}
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 -ml-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        )}

        {/* Desktop Search Bar */}
        <div className="hidden lg:block flex-1 max-w-2xl">
          <div className="relative">
            <Search
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-transparent rounded-lg focus:bg-white focus:border-primary-300 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
            />
          </div>
        </div>

        {/* Mobile Search - Collapsed */}
        <div className="lg:hidden flex-1">
          {isMobileSearchOpen ? (
            <div className="relative flex items-center">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                autoFocus
                className="w-full pl-9 pr-10 py-2 bg-gray-100 border border-transparent rounded-lg focus:bg-white focus:border-primary-300 focus:ring-2 focus:ring-primary-100 outline-none transition-all text-sm"
              />
              <button
                onClick={() => {
                  setIsMobileSearchOpen(false)
                  onSearchChange('')
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                aria-label="Close search"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Open search"
            >
              <Search size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Right Section - Actions */}
      <div className="flex items-center gap-1 lg:gap-3 ml-2 lg:ml-4">
        {/* Sync Status Display - Desktop Only */}
        {(syncMessage || syncStatus) && (
          <div className="hidden lg:flex items-center gap-2 text-sm">
            {isSyncing ? (
              <>
                <Loader2 size={16} className="animate-spin text-primary-600" />
                <span className="text-primary-600 font-medium">{syncMessage}</span>
              </>
            ) : syncStatus?.isComplete ? (
              <>
                <Check size={16} className="text-green-600" />
                <span className="text-gray-600">
                  {syncStatus.totalSynced} emails
                  {syncStatus.oldestDate && (
                    <span className="text-gray-400"> (from {syncStatus.oldestDate} to now)</span>
                  )}
                </span>
              </>
            ) : syncStatus ? (
              <span className="text-amber-600">
                {syncStatus.totalSynced} emails - sync incomplete
              </span>
            ) : null}
          </div>
        )}

        {/* Mobile Sync Status - Condensed */}
        {isSyncing && (
          <div className="lg:hidden flex items-center">
            <Loader2 size={16} className="animate-spin text-primary-600" />
          </div>
        )}

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className={`flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-1.5 rounded-lg transition-colors ${
            isLoading
              ? 'text-primary-600 bg-primary-50'
              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          title={isSyncing ? 'Syncing...' : 'Sync emails'}
        >
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          <span className="hidden lg:inline text-sm font-medium">
            {isSyncing ? 'Syncing' : 'Sync'}
          </span>
        </button>

        {/* Settings Button */}
        <div className="relative">
          <button
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            className={`p-2 rounded-lg transition-colors ${
              isSettingsOpen
                ? 'text-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="Settings"
          >
            <Settings size={20} />
          </button>

          {isSettingsOpen && (
            <SettingsPanel onClose={() => setIsSettingsOpen(false)} />
          )}
        </div>
      </div>
    </header>
  )
}
